import { reservedEditableIds } from '../shared/editable-id-repair.js';
import { KIND_ATTR, stampElementKinds } from '../shared/element-kind.js';

/**
 * Centralized utilities for injecting, finding, and stripping editable
 * element attributes. Single source of truth — all modules import from here.
 */

import { normalizeLogoSlots } from './logo-normalizer.js';
import { ENTRANCE_ATTR, ENTRANCE_DELAY_ATTR } from '../shared/entrance-presets.js';
import { BEHAVIOUR_ATTR, BEHAVIOUR_BOUND_PREFIX } from '../shared/behaviour-contract.js';
import { LOGO_WRAPPER_SELECTOR, isLogoEditableWrapper } from './logo-dom.js';

/**
 * Typed editable map — maps editable type to CSS selectors.
 * Used by injectEditableAttrs() to stamp the correct data-pb-editable value.
 */
export const EDITABLE_MAP = {
  text: [
    // Core text
    '.cs-topper',
    '.cs-title',
    '.cs-text',
    '.cs-h3',
    '.cs-item-p',
    '.cs-item-text',
    '.cs-name',
    '.cs-job',
    // Headings
    '.cs-header',
    '.cs-h2',
    '.cs-h4',
    '.cs-heading',
    '.cs-heading3',
    // Stats & numbers
    '.cs-number',
    '.cs-stat',
    '.cs-stat-number',
    '.cs-stat-text',
    // Descriptions & details
    '.cs-desc',
    '.cs-label',
    '.cs-price',
    '.cs-package',
    '.cs-date',
    '.cs-time',
    // Lists & quotes
    '.cs-li-text',
    '.cs-quote-text',
    '.cs-blockquote',
    // Reviews
    '.cs-review-name',
    '.cs-review-title',
    // CTA & forms
    '.cs-cta-title',
    '.cs-cta-text',
    '.cs-form-title',
    // Links (text-style, not button-style)
    '.cs-link',
    '.cs-li-link',
    '.cs-fake-link',
    '.cs-phone',
    '.cs-email',
    '.cs-address',
    // Button inner text span
    '.cs-button-text',
    // Off-vocabulary classes used by scraped template sections
    '.cs-tag',
    '.cs-link-content',
    '.cs-top-link',
    '.cs-item-number',
    '.cs-form-text',
    '.cs-checkbox-label',
    '.cs-slogan',
    '.cs-item-header',
    '.cs-item-desc',
    '.cs-banner-title',
    // Repeated-item fields found by the signature corpus. Keep these explicit
    // so an intentionally empty sibling receives an eid too; otherwise bulk
    // restyling can resolve the right node but has no override key to write.
    '.cs-location',
    '.price-item'
  ],
  button: [
    '.cs-button-solid',
    '.cs-button-transparent',
    '.cs-button',
    '.cs-btn',
    '.cs-submit',
    '.cs-submit-button'
  ],
  // Structural wrappers we want selectable for reset/delete actions only.
  // These are intentionally NOT text-editable.
  container: ['.cs-item']
};

/** Back-compat flat selector list for callers that just need all selectors. */
export const EDITABLE_SELECTORS = Object.values(EDITABLE_MAP).flat();

// Single quotes in this data URI MUST stay encoded as %27 — this string is
// injected into a single-quoted JS literal inside the `onerror` attribute, and
// raw single quotes would prematurely terminate the string and trigger
// "SyntaxError: Unexpected identifier 'http'" in the iframe.
const BROKEN_IMG_SVG = `data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27150%27 fill=%27%23999%27%3E%3Crect width=%27200%27 height=%27150%27 fill=%27%23eee%27/%3E%3Ctext x=%2750%25%27 y=%2750%25%27 text-anchor=%27middle%27 dy=%27.3em%27 font-family=%27system-ui%27 font-size=%2714%27%3EImage not found%3C/text%3E%3C/svg%3E`;
// LOGO_WRAPPER_SELECTOR / LOGO_INNER_SELECTOR and the wrapper predicate are
// owned by ./logo-dom.js. This module's notion is the STRICTER one
// (`isLogoEditableWrapper`): stamping a slot with nothing in it would offer the
// user a logo that is not there.

const TEXT_FALLBACK_TAGS = new Set([
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'P',
  'LI',
  'A',
  'LABEL',
  'SUMMARY',
  'BLOCKQUOTE',
  'FIGCAPTION',
  'DT',
  'DD',
  'SPAN',
  'DIV',
  'TD',
  'TH',
  'CAPTION',
  'STRONG',
  'EM',
  'TIME',
  'ADDRESS',
  'SMALL',
  'CITE',
  'LEGEND',
  'ABBR',
  'OUTPUT',
  'BDI',
  'BDO',
  'BDT',
  'DEL',
  'DFN',
  'INS',
  'KBD',
  'MARK',
  'Q',
  'S',
  'SAMP',
  'SUB',
  'SUP',
  'U',
  'VAR'
]);

/** Marker attr for spans injected around orphan text nodes (Tier 4 Phase 1). */
const TEXT_WRAP_ATTR = 'data-pb-textwrap';
const TEXT_ROOT_ATTR = 'data-pb-text-root';
/** Persisted structural marker for inferred repeated cards/items. */
const REPEAT_ITEM_ATTR = 'data-pb-repeat-item';
/**
 * Persisted marker for freeform BLOCK ROOTS whose template carries no
 * editable of its own (lists, button anchors, forms, rows, cards, dividers,
 * embeds). Distinct from REPEAT_ITEM_ATTR on purpose: repeat items feed the
 * generic repeat adapter's sibling enumeration, while block roots are the
 * freeform-block adapter's units — sharing the attribute would split one
 * freeform selection across two adapter kinds. Survives stripTempAttrs like
 * data-pb-eid; removed at export by stripAllBuilderAttrs like all data-pb-*.
 */
export const BLOCK_ROOT_ATTR = 'data-pb-block-root';
const REPEAT_ITEM_TAGS = new Set(['ARTICLE', 'LI', 'DIV', 'A', 'FIGURE']);
const REPEAT_CLASS_HINT =
  /(?:^|[-_])(?:article|card|product|item|tile|post|result|collection|feature|service|testimonial|pricing?|team|slide|faq|accordion)(?:[-_]|$)/i;
const REPEAT_PARENT_HINT =
  /(?:^|[-_])(?:grid|cards?|articles?|products?|items?|list|collection|results?|slider|carousel|row)(?:[-_]|$)/i;
const UNSTABLE_REPEAT_CLASS =
  /^(?:active|current|selected|open|closed|first|last|odd|even|hidden|visible|is-|has-|js-)|^(?:cs-)?item\d+$/i;

function _containsMeaningfulCharacters(value) {
  return /[\p{L}\p{N}]/u.test(String(value || ''));
}

function _classLike(el) {
  const cls = (el.getAttribute('class') || '').toLowerCase();
  const id = (el.getAttribute('id') || '').toLowerCase();
  return `${cls} ${id}`.trim();
}

function _hasMeaningfulText(el) {
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (!text) return false;
  return _containsMeaningfulCharacters(text);
}

function _hasDirectMeaningfulText(el) {
  if (!el) return false;
  for (const node of Array.from(el.childNodes || [])) {
    if (node.nodeType !== 3) continue; // TEXT_NODE
    const text = String(node.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (text && _containsMeaningfulCharacters(text)) return true;
  }
  return false;
}

function _hasMediaDescendant(el) {
  return !!el?.querySelector?.('img, picture, svg, video, canvas, audio');
}

function _isLikelyButton(el) {
  if (!el) return false;
  if (el.closest('svg')) return false;
  if (
    el.matches(
      'button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"]'
    )
  )
    return true;

  const cls = _classLike(el);
  if (/\b(cs-button|button|btn|cta|submit)\b/.test(cls)) return true;

  if (el.tagName === 'A') {
    if (/\b(button|btn|cta)\b/.test(cls)) return true;
    // Links used as form submit/action buttons
    if (el.closest('form')) return true;
  }

  return false;
}

function _isInsideButtonContext(el) {
  return !!el.closest(
    '[data-pb-editable="button"], button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"], a[class*="button"], a[class*="btn"], .cs-button, .cs-button-solid, .cs-button-transparent, .cs-btn, .cs-submit, .cs-submit-button'
  );
}

function _isFallbackTextCandidate(el) {
  if (!el) return false;
  // data-pb-eid is persisted specifically to preserve element identity. Once a
  // text-like fallback has that identity, keep it editable even when the user
  // empties it; otherwise the next render makes it impossible to select again.
  const persistedEmptyText = el.hasAttribute('data-pb-eid') && !_hasMeaningfulText(el);
  if (!persistedEmptyText && !_hasMeaningfulText(el)) return false;
  if (el.closest('svg')) return false;
  if (!TEXT_FALLBACK_TAGS.has(el.tagName) && !String(el.localName || '').includes('-'))
    return false;
  // Never tag a container fallback if it already contains explicitly-tagged editables.
  // This prevents parent wrappers (e.g. card <li>) from becoming text-editable and
  // accidentally flattening their image/heading children during text edits.
  if (el.querySelector('[data-pb-editable]')) return false;
  if (_hasMediaDescendant(el)) return false;
  if (el.tagName === 'A' && _isLikelyButton(el)) return false;
  if (_isInsideButtonContext(el)) return false;
  if (el.tagName === 'SPAN' || el.tagName === 'DIV') {
    if (!_hasDirectMeaningfulText(el)) return false;
    // A leaf span inside an uneditable card-wrapping link is still valid text.
    // Only suppress it when a real editable text ancestor already owns it.
    if (el.parentElement?.closest('[data-pb-editable="text"]')) return false;
  }
  return true;
}

/**
 * Wrap one bare text node in an editable marker <span>, keeping leading/
 * trailing whitespace OUTSIDE the wrapper so inter-element spacing is
 * unchanged. Returns true when a wrapper was inserted.
 */
function _wrapTextNode(node) {
  const raw = String(node.textContent || '');
  const leading = raw.match(/^\s*/)[0];
  const trailing = raw.length > leading.length ? raw.match(/\s*$/)[0] : '';
  const core = raw.slice(leading.length, raw.length - trailing.length);
  if (!core || !_containsMeaningfulCharacters(core)) return false;

  const doc = node.ownerDocument;
  const parent = node.parentNode;
  if (!doc || !parent) return false;

  const span = doc.createElement('span');
  span.setAttribute(TEXT_WRAP_ATTR, '1');
  span.textContent = core;
  span.setAttribute('data-pb-editable', 'text');

  if (leading) parent.insertBefore(doc.createTextNode(leading), node);
  parent.insertBefore(span, node);
  if (trailing) parent.insertBefore(doc.createTextNode(trailing), node);
  parent.removeChild(node);
  return true;
}

/**
 * Wrap orphan meaningful text nodes in editable spans (Tier 4 Phase 1).
 * Mixed-content only: a bare text node is wrapped ONLY when its parent also
 * has at least one element child (e.g. <li><img> text</li>, "Read More <img>",
 * "Deal Value: <span>…</span>"). Pure-text containers stay on the existing
 * whole-element fallback path. Wrappers persist in stored builder HTML
 * (data-pb-textwrap survives stripTempAttrs) and are unwrapped only at export
 * by unwrapTextWrappers() inside stripAllBuilderAttrs().
 */
function _wrapOrphanTextNodes(root) {
  // Re-tag persisted wrappers first — stored HTML carries data-pb-textwrap
  // (+ data-pb-eid) but data-pb-editable was stripped by stripTempAttrs.
  for (const span of root.querySelectorAll(`[${TEXT_WRAP_ATTR}]`)) {
    _setEditableType(span, 'text');
  }

  for (const parent of root.querySelectorAll(Array.from(TEXT_FALLBACK_TAGS).join(','))) {
    const parentType = parent.getAttribute('data-pb-editable');
    if (parentType && parentType !== 'container') continue;
    // Mixed-content gate: bare text must sit alongside an element sibling.
    if (!parent.firstElementChild) continue;
    if (!_hasDirectMeaningfulText(parent)) continue;
    if (parent.closest('svg')) continue;
    if (parent.closest(`[${TEXT_WRAP_ATTR}]`)) continue;
    // Already-managed regions: text edits own their subtree; buttons and
    // logos have dedicated editors.
    if (parent.closest('[data-pb-editable="text"], [data-pb-editable="logo"]')) continue;
    if (_isInsideButtonContext(parent)) continue;

    for (const node of Array.from(parent.childNodes)) {
      if (node.nodeType !== 3) continue; // TEXT_NODE
      _wrapTextNode(node);
    }
  }
}

function _setEditableType(el, type) {
  if (!el || el.hasAttribute('data-pb-editable')) return;
  const logoWrapper = el.closest?.('[data-pb-editable="logo"]');
  if (logoWrapper && logoWrapper !== el) return;
  el.setAttribute('data-pb-editable', type);
}

function _stableRepeatClasses(el) {
  if (!el?.classList) return [];
  return Array.from(el.classList).filter(
    (className) =>
      className &&
      !UNSTABLE_REPEAT_CLASS.test(className) &&
      !/^(?:col|span|order)-?\d+$/i.test(className)
  );
}

function _sharesRepeatClass(classNames, el) {
  if (!classNames.length || !el?.classList) return false;
  return classNames.some((className) => el.classList.contains(className));
}

function _isCompositeRepeatCandidate(el) {
  if (!el || !REPEAT_ITEM_TAGS.has(el.tagName)) return false;
  if (!_hasMeaningfulText(el) && !_hasMediaDescendant(el)) return false;
  const directElements = el.children?.length || 0;
  const descendantElements = el.querySelectorAll?.('*').length || 0;
  return (
    directElements >= 2 ||
    descendantElements >= 3 ||
    (_hasMediaDescendant(el) && directElements >= 1)
  );
}

/**
 * Infer repeated card/item roots from sibling structure, not a vendor prefix.
 * The persisted marker gives context-menu and keyboard actions one stable,
 * export-clean selector for CodeStitch, scraped, and Shopify-style cards.
 */
function _stampRepeatItems(root) {
  for (const persisted of root.querySelectorAll(`[${REPEAT_ITEM_ATTR}]`)) {
    _setEditableType(persisted, 'container');
  }

  const parents = [root, ...root.querySelectorAll('*')];
  for (const parent of parents) {
    const children = Array.from(parent.children || []).filter(_isCompositeRepeatCandidate);
    if (children.length < 2) continue;
    const parentClasses = _stableRepeatClasses(parent);
    const parentLooksRepeated = parentClasses.some((className) =>
      REPEAT_PARENT_HINT.test(className)
    );

    for (const child of children) {
      const classes = _stableRepeatClasses(child);
      if (!classes.length) continue;
      const childLooksRepeated = classes.some((className) => REPEAT_CLASS_HINT.test(className));
      if (!childLooksRepeated && !parentLooksRepeated) continue;
      const peers = children.filter(
        (candidate) => candidate.tagName === child.tagName && _sharesRepeatClass(classes, candidate)
      );
      if (peers.length < 2) continue;
      for (const peer of peers) {
        peer.setAttribute(REPEAT_ITEM_ATTR, '');
        _setEditableType(peer, 'container');
      }
    }
  }
}

function _collectUniqueCandidates(root, selectors) {
  const seen = new Set();
  const out = [];
  for (const selector of selectors) {
    for (const el of root.querySelectorAll(selector)) {
      if (seen.has(el)) continue;
      seen.add(el);
      out.push(el);
    }
  }
  return out;
}

function _scrubLogoDescendantEditables(wrapper) {
  if (!wrapper?.querySelectorAll) return;
  for (const el of wrapper.querySelectorAll('[data-pb-editable]')) {
    el.removeAttribute('data-pb-editable');
  }
}

function _stampLogoWrappers(root) {
  const wrappers = new Set();
  if (isLogoEditableWrapper(root)) wrappers.add(root);
  for (const wrapper of root.querySelectorAll(LOGO_WRAPPER_SELECTOR)) {
    if (isLogoEditableWrapper(wrapper)) wrappers.add(wrapper);
  }

  for (const wrapper of wrappers) {
    _scrubLogoDescendantEditables(wrapper);
    wrapper.setAttribute('data-pb-editable', 'logo');
  }
}

/**
 * Inject temporary data-pb-editable attrs onto a parsed DOM root for
 * element identification. Also adds onerror fallback on images.
 */
export function injectEditableAttrs(root) {
  normalizeLogoSlots(root);
  _stampLogoWrappers(root);

  // 1) Buttons first so nested button text doesn't get tagged as standalone text.
  const explicitButtonCandidates = _collectUniqueCandidates(root, EDITABLE_MAP.button);
  for (const el of explicitButtonCandidates) {
    if (_isLikelyButton(el)) _setEditableType(el, 'button');
  }
  for (const el of root.querySelectorAll(
    'a, button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"]'
  )) {
    if (_isLikelyButton(el)) _setEditableType(el, 'button');
  }

  // 2) Structural containers (selectable-only wrappers), including inferred
  // repeat roots that do not use CodeStitch's .cs-item vocabulary.
  // Persisted freeform block roots classify as containers every render pass.
  for (const el of root.querySelectorAll(`[${BLOCK_ROOT_ATTR}]`)) {
    _setEditableType(el, 'container');
  }
  _stampRepeatItems(root);
  const explicitContainerCandidates = _collectUniqueCandidates(root, EDITABLE_MAP.container || []);
  for (const el of explicitContainerCandidates) {
    if (_hasMeaningfulText(el) && !el.firstElementChild) continue;
    _setEditableType(el, 'container');
  }

  // Slash-created semantic roots (UL/DIV/etc.) retain their text-editing
  // identity across render passes. The marker is builder-only and is removed
  // by stripAllBuilderAttrs during export.
  for (const el of root.querySelectorAll(`[${TEXT_ROOT_ATTR}]`)) {
    el.setAttribute('data-pb-editable', 'text');
  }

  // 3) Explicit text selectors + semantic text fallback.
  const explicitTextCandidates = _collectUniqueCandidates(root, EDITABLE_MAP.text);
  for (const el of explicitTextCandidates) {
    if (_isInsideButtonContext(el)) continue;
    // Wrapper links that contain nested editable/media content should not be
    // text-editable themselves, otherwise they swallow clicks needed to select
    // parent card containers (e.g. .cs-item reset/delete actions).
    if (el.tagName === 'A') {
      if (el.querySelector('img, picture, svg')) continue;
      if (el.querySelector(EDITABLE_SELECTORS.join(','))) continue;
    }
    // Keep explicit text selectors editable even when empty.
    // This prevents "empty == disappears" behavior after a user clears content.
    _setEditableType(el, 'text');
  }
  for (const el of root.querySelectorAll(Array.from(TEXT_FALLBACK_TAGS).join(','))) {
    if (_isFallbackTextCandidate(el)) _setEditableType(el, 'text');
  }
  for (const el of root.querySelectorAll('*')) {
    if (String(el.localName || '').includes('-') && _isFallbackTextCandidate(el)) {
      _setEditableType(el, 'text');
    }
  }

  // 3.5) Orphan text nodes in mixed-content containers (Tier 4 Phase 1).
  // Runs after text tagging (so tagged subtrees are skipped) and before
  // images (wrap decisions don't depend on image tagging).
  _wrapOrphanTextNodes(root);

  // 4) Images and SVGs.
  for (const img of root.querySelectorAll('img')) {
    if (img.closest('[data-pb-editable="logo"]')) continue;
    img.setAttribute('data-pb-editable', 'image');
    const existingOnerror = img.getAttribute('onerror') || '';
    if (!existingOnerror.includes('pb-broken-img')) {
      img.setAttribute(
        'onerror',
        `this.onerror=null;this.src='${BROKEN_IMG_SVG}';this.classList.add('pb-broken-img')`
      );
    }
  }
  for (const svg of root.querySelectorAll('svg')) {
    // A logo wrapper owns all of its mark slots as one replaceable thing. Every
    // other nested graphic is a first-class target, including button icons.
    if (svg.closest('[data-pb-editable="logo"]')) continue;
    svg.setAttribute('data-pb-editable', 'svg');
  }

  // 5) Inert-family editables (Tier 4 Phase 5): embeds, media, form fields.
  // Each gets a minimal focused editor (embed/video src, select options,
  // input/textarea placeholder). _setEditableType refuses already-tagged
  // elements, so button-type inputs (pass 1) and logo internals keep their
  // existing classification.
  for (const el of root.querySelectorAll('iframe')) {
    _setEditableType(el, 'embed');
  }
  for (const el of root.querySelectorAll('video, audio')) {
    _setEditableType(el, 'media');
  }
  for (const el of root.querySelectorAll('select, textarea')) {
    _setEditableType(el, 'field');
  }
  for (const el of root.querySelectorAll('input')) {
    const inputType = (el.getAttribute('type') || 'text').toLowerCase();
    if (
      inputType === 'button' ||
      inputType === 'submit' ||
      inputType === 'reset' ||
      inputType === 'hidden'
    )
      continue;
    _setEditableType(el, 'field');
  }

  stampElementKinds(root);
}

/**
 * Strip ONLY temporary injected attrs (data-pb-editable, onerror).
 * PRESERVES data-pb-eid (persisted in stored HTML).
 */
export function stripTempAttrs(root) {
  for (const el of root.querySelectorAll('[data-pb-editable]')) {
    el.removeAttribute('data-pb-editable');
  }
  // AG-4: a behaviour runtime writes `data-pb-bound-<id>` when it binds, and the
  // marker is TRANSIENT — it describes this document's listeners, which do not
  // survive being saved and reloaded. Persisting it would make the next render
  // skip a host nothing has bound, and the widget would be dead with a marker
  // claiming otherwise. Prefix-matched deliberately: the ids are open-ended, and
  // this is a REMOVAL, so a broad match is the safe direction (unlike
  // stripAllBuilderAttrs' carve-outs, which are exact names because a broad
  // match there would PRESERVE something unaudited).
  for (const el of root.querySelectorAll('*')) {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith(BEHAVIOUR_BOUND_PREFIX)) el.removeAttribute(attr.name);
    }
  }
  for (const img of root.querySelectorAll('img[onerror]')) {
    img.removeAttribute('onerror');
  }
  for (const el of root.querySelectorAll(`[${KIND_ATTR}]`)) {
    el.removeAttribute(KIND_ATTR);
  }
}

/**
 * Replace every injected orphan-text wrapper span with its own childNodes,
 * restoring the original bare-text markup (Tier 4 Phase 1). Preserves any
 * inline formatting the user applied inside the wrapper (e.g. <strong>).
 * Export-only companion to _wrapOrphanTextNodes().
 */
export function unwrapTextWrappers(root) {
  for (const span of root.querySelectorAll(`[${TEXT_WRAP_ATTR}]`)) {
    const parent = span.parentNode;
    if (!parent) continue;
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
  }
}

/**
 * Strip ALL builder attrs for export (data-pb-*, onerror, contenteditable), plus
 * inherited third-party analytics hooks. This removes data-pb-eid too — export only.
 */
export function stripAllBuilderAttrs(root) {
  // Unwrap injected orphan-text spans FIRST so export HTML is byte-equivalent
  // to the original bare-text markup (no residual wrapper elements).
  unwrapTextWrappers(root);
  for (const el of root.querySelectorAll('*')) {
    for (const attr of Array.from(el.attributes)) {
      // Client binding markers are semantic (resolved later by
      // rewriteClientLinksInHTML, which also removes them after resolving).
      if (attr.name === 'data-pb-client' || attr.name === 'data-pb-client-text') continue;
      // Canonical exports use category-derived ids (`navigation`, not
      // `cs-navigation-*`). The shipped nav runtime locates that root through
      // this semantic marker, so stripping it makes every canonical mobile
      // toggle a no-op.
      if (attr.name === 'data-pb-nav-root') continue;
      // Mobile-menu fragment markers are LOAD-BEARING in exports (MM-5): the
      // merge's desktop-safety rule hides `[data-pb-mm-deco]`/`[data-pb-mm-chrome]`
      // above the mobile edge — stripping them would leak drawer chrome into
      // the desktop bar on every exported fragment-dressed site.
      if (attr.name.startsWith('data-pb-mm-')) continue;
      // Motion stamps are LOAD-BEARING in exports too (found by IF-8's first
      // real 11ty build). The parser writes `data-pb-motion="<pattern>"`, the
      // collapse/fallback CSS keys on it, and the shipped runtime's drivers
      // select on it. Stripping it left the exported page carrying BOTH the
      // captured stylesheet's invisible rest state (`.rotating-text-word span
      // { opacity: 0 }`) and the fallback rule that overrides it — with the
      // rule matching nothing. The exported rotating headline was blank, and
      // M7's parallax and marquee drivers had been inert in every export since
      // they shipped, because detection keys on authored markers (which
      // survive) while the drivers key on this stamp (which did not).
      if (attr.name.startsWith('data-pb-motion') || attr.name.startsWith('data-pb-rotator')) {
        continue;
      }
      // Builder entrance attributes are the emitted product (EA-2), so they
      // must survive the strip that removes every other `data-pb-*`.
      //
      // EXACT NAMES, deliberately not a `data-pb-reveal` prefix: a prefix would
      // also preserve any future or malformed `data-pb-reveal-*` attribute
      // nobody has audited, and this list is the last thing standing between
      // editor state and shipped bytes. Two names, both owned by
      // shared/entrance-presets.js, both asserted equal to it by test.
      if (attr.name === ENTRANCE_ATTR || attr.name === ENTRANCE_DELAY_ATTR) continue;
      // AG-4: the behaviour stamp is the emitted product. The exported site's
      // runtime finds its host through this attribute and through nothing else,
      // so stripping it ships the markup of a working widget with no way to
      // reach it — a tab strip that renders and cannot be clicked.
      //
      // EXACT NAME, and note what is deliberately NOT here: `data-pb-bound-*`.
      // That marker is transient, and preserving it would ship a document whose
      // hosts are all marked already-bound, so the runtime would decline every
      // one of them. The two attributes come from the same feature and must be
      // treated in opposite ways, which is why they have different names rather
      // than a shared prefix.
      if (attr.name === BEHAVIOUR_ATTR) continue;
      if (attr.name.startsWith('data-pb-')) {
        el.removeAttribute(attr.name);
      }
      if (attr.name === 'data-action' || attr.name === 'data-instance') {
        el.removeAttribute(attr.name);
      }
      // Umami auto-tracks data-umami-event attributes. Scraped or imported
      // markup can carry them from whatever site it came from, which would
      // double-count against the delegated analytics-events.js under event
      // names belonging to somebody else's dashboard.
      if (attr.name === 'data-umami-event' || attr.name.startsWith('data-umami-event-')) {
        el.removeAttribute(attr.name);
      }
    }
    el.removeAttribute('onerror');
    el.removeAttribute('contenteditable');
  }
}

/**
 * Escape user-provided value for use inside an attribute selector.
 * Falls back to conservative escaping when CSS.escape is unavailable.
 */
export function escapeForAttrSelector(value) {
  const raw = String(value == null ? '' : value);
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(raw);
  }
  return raw.replace(/[^A-Za-z0-9_-]/g, (ch) => `\\${ch}`);
}

/**
 * Parse HTML string, inject editables, find nth editable, return element (or null).
 */
export function findEditableByIndex(html, index) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return null;
  injectEditableAttrs(root);
  const allEditables = root.querySelectorAll('[data-pb-editable]');
  return index < allEditables.length ? allEditables[index] : null;
}

/**
 * Parse HTML string, find element with data-pb-eid="X", return element (or null).
 */
export function findEditableByEid(html, eid) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  injectEditableAttrs(root);
  return root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
}

/**
 * Extract display metadata for an editable by eid or index.
 */
export function extractEditableMeta(html, eid, editableIndex, fallbackTagName = '') {
  const emptyMeta = {
    tagName: String(fallbackTagName || '').toUpperCase() || 'TEXT',
    classNames: [],
    elementId: '',
    textContent: '',
    kind: 'unknown'
  };
  if (!html) return emptyMeta;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return emptyMeta;

  injectEditableAttrs(root);
  let target = null;
  if (eid) {
    target = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
  }
  if (!target && typeof editableIndex === 'number') {
    const allEditables = root.querySelectorAll('[data-pb-editable]');
    target = allEditables[editableIndex] || null;
  }
  if (!target) {
    stripTempAttrs(root);
    return emptyMeta;
  }

  const meta = {
    tagName: (target.tagName || emptyMeta.tagName || 'TEXT').toUpperCase(),
    classNames: Array.from(target.classList || []),
    elementId: target.getAttribute('id') || '',
    textContent: target.textContent || '',
    kind: target.getAttribute(KIND_ATTR) || 'unknown'
  };
  stripTempAttrs(root);
  return meta;
}

/**
 * Update an editable element's innerHTML by eid.
 * Returns { html, editableType } or null when eid is not found.
 */
export function updateEditableByEidInHTML(html, eid, newInnerHTML, options = {}) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  injectEditableAttrs(root);

  const target = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
  if (!target) return null;

  const originalTarget = target.cloneNode(true);
  target.innerHTML = newInnerHTML;
  if (typeof options.onTargetUpdated === 'function') {
    options.onTargetUpdated(target, originalTarget);
  }

  const editableType = target.getAttribute('data-pb-editable') || null;
  stripTempAttrs(root);
  return { html: root.innerHTML, editableType };
}

function isSafeAttrName(name) {
  const value = String(name || '').trim();
  return /^[A-Za-z_:][A-Za-z0-9_:.:-]*$/.test(value) && !value.toLowerCase().startsWith('on');
}

/**
 * Update attributes on an element targeted by data-pb-eid.
 * Returns { html, changed, found }.
 */
export function updateElementAttrsInHTML(html, eid, { id, classNames, attrs } = {}) {
  if (!html || !eid) return { html, changed: false, found: false };
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return { html, changed: false, found: false };

  const target = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
  if (!target) return { html, changed: false, found: false };

  let changed = false;
  if (typeof id === 'string') {
    const nextId = id.trim();
    const currentId = target.getAttribute('id') || '';
    if (currentId !== nextId) {
      if (nextId) target.setAttribute('id', nextId);
      else target.removeAttribute('id');
      changed = true;
    }
  }

  if (Array.isArray(classNames)) {
    const normalized = classNames.map((name) => String(name || '').trim()).filter(Boolean);
    const current = Array.from(target.classList || []);
    if (current.join(' ') !== normalized.join(' ')) {
      if (normalized.length > 0) target.setAttribute('class', normalized.join(' '));
      else target.removeAttribute('class');
      changed = true;
    }
  }

  if (attrs && typeof attrs === 'object') {
    for (const [rawName, rawValue] of Object.entries(attrs)) {
      const name = String(rawName || '').trim();
      if (!isSafeAttrName(name)) continue;
      const nextValue = rawValue == null ? '' : String(rawValue);
      const currentValue = target.getAttribute(name) || '';
      if (nextValue) {
        if (currentValue !== nextValue) {
          target.setAttribute(name, nextValue);
          changed = true;
        }
      } else if (target.hasAttribute(name)) {
        target.removeAttribute(name);
        changed = true;
      }
    }
  }

  return { html: changed ? root.innerHTML : html, changed, found: true };
}

/**
 * Delete an editable from HTML by eid — returns updated HTML string (or null if not found).
 */
export function deleteEditableFromHTML(html, eid) {
  if (!eid) return null;
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  let el = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
  if (!el) {
    injectEditableAttrs(root);
    el = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
  }
  if (!el) return null;
  el.remove();
  stripTempAttrs(root);
  return root.innerHTML;
}

/**
 * Stamp data-pb-eid on any editable element that lacks one.
 * Called at section add/load time, NOT during render.
 * Returns { html: string, stamped: boolean }.
 */
export function stampEditableIds(html, { editableIdRepairs, freshBaseline = false } = {}) {
  const reserved = reservedEditableIds(editableIdRepairs);
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  let stamped = false;
  if (freshBaseline) {
    for (const el of root.querySelectorAll('[data-pb-eid]')) {
      if (!reserved.has(el.getAttribute('data-pb-eid'))) continue;
      el.removeAttribute('data-pb-eid');
      stamped = true;
    }
  }
  injectEditableAttrs(root);
  let counter = 0;
  for (const el of root.querySelectorAll('[data-pb-editable]')) {
    if (!el.getAttribute('data-pb-eid')) {
      while (reserved.has(`e${++counter}`) || root.querySelector(`[data-pb-eid="e${counter}"]`)) {
        /* advance until the id is free — the ++counter in the test is the body */
      }
      el.setAttribute('data-pb-eid', `e${counter}`);
      stamped = true;
    }
  }
  stripTempAttrs(root);
  return { html: root.innerHTML, stamped };
}

/**
 * Prune override entries whose eid no longer exists in HTML.
 * Call after any HTML mutation (delete element, swap section).
 * Returns pruned elements object, or null if empty.
 */
const PICTURE_OVERRIDE_KEYS = [
  'pictureWidth',
  'pictureMaxWidth',
  'pictureMarginTop',
  'pictureMarginRight',
  'pictureMarginBottom',
  'pictureMarginLeft',
  'picturePaddingTop',
  'picturePaddingRight',
  'picturePaddingBottom',
  'picturePaddingLeft',
  'pictureLeft'
];

export function pruneOrphanOverrides(html, elementOverrides) {
  if (!elementOverrides) return null;
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const existingEids = new Set(
    Array.from(root.querySelectorAll('[data-pb-eid]')).map((el) => el.getAttribute('data-pb-eid'))
  );
  const pruned = {};
  for (const [eid, overrides] of Object.entries(elementOverrides)) {
    if (!existingEids.has(eid)) continue;

    // Strip picture* props if element no longer has a <picture> parent
    const el = root.querySelector(`[data-pb-eid="${eid}"]`);
    const hasPicture =
      el && (el.closest('picture') || (el.parentElement && el.parentElement.tagName === 'PICTURE'));
    if (!hasPicture && overrides && typeof overrides === 'object') {
      const cleaned = { ...overrides };
      let changed = false;
      for (const key of PICTURE_OVERRIDE_KEYS) {
        if (key in cleaned) {
          delete cleaned[key];
          changed = true;
        }
      }
      pruned[eid] =
        changed && Object.keys(cleaned).length === 0 ? undefined : changed ? cleaned : overrides;
      if (pruned[eid] === undefined) delete pruned[eid];
    } else {
      pruned[eid] = overrides;
    }
  }
  return Object.keys(pruned).length > 0 ? pruned : null;
}

/**
 * Stamp all sections in a full state object. Mutates state in place.
 * Called on all load paths (JSON, ZIP, auto-save) to migrate old projects.
 * Returns true if any section was stamped.
 */
export function stampAllSections(state) {
  let anyStamped = false;
  const allSections = [];
  if (state.sharedSections?.navigation) allSections.push(state.sharedSections.navigation);
  if (state.sharedSections?.footer) allSections.push(state.sharedSections.footer);
  for (const page of state.pages) {
    allSections.push(...page.sections);
  }
  for (const section of allSections) {
    if (!section.html) continue;
    // Recorded identity must not be silently rewritten by a load migration.
    if (Object.hasOwn(section, 'editableIdRepairs')) continue;
    const { html, stamped } = stampEditableIds(section.html);
    section.html = html;
    if (stamped) anyStamped = true;

    // Legacy projects may be missing originalHtml entirely.
    // Seed it from stamped section HTML so element reset has a stable baseline.
    if (typeof section.originalHtml !== 'string' || !section.originalHtml.trim()) {
      section.originalHtml = html;
      anyStamped = true;
      continue;
    }

    // Also stamp original snapshots so reset-by-eid can reliably find elements.
    const { html: originalStamped, stamped: originalWasStamped } = stampEditableIds(
      section.originalHtml
    );
    section.originalHtml = originalStamped;
    if (originalWasStamped) anyStamped = true;
  }
  return anyStamped;
}

export { dedupeEditableIdsAcrossState, remapEidsInSection } from './editable-id-repair-planner.js';
